import AccommodationIndividualSelectRoomOptionTile from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTile'
import AccommodationIndividualSelectRoomOptionTileDetails from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileDetails'
import AccommodationIndividualSelectRoomOptionTilePricing from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTilePricing'
import React, { useCallback, useContext, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import WalledContent from 'components/Common/WalledContent'
import moment from 'moment'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import AccommodationIndividualSelectRoomOptionTileCTA from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileCTA'
import TextButton from 'LuxKit/Button/TextButton'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { getPlural } from 'lib/string/pluralize'
import AccommodationIndividualSelectRoomOptionTileMessage from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileMessage'
import { rem } from 'polished'
import styled from 'styled-components'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import MultipleRoomsBookingModalPriceDetails from 'components/OfferPage/MultipleRoomsBooking/MultipleRoomsBookingModalPriceDetails'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import MultipleRoomsInclusions from 'components/OfferPage/MultipleRoomsBooking/MultipleRoomsInclusions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import { getRemainingAvailabilityByPackage } from 'selectors/offerPage/multipleRoomBookingSelectors'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'

const LoyaltyPointsWrapper = styled.div`
  padding-block: ${rem(2)}; // To compensate for the loyalty coin which protrudes a couple of pixels outside of its container

  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  packageNumber: number
  packageView: App.OfferPackageOptionView
  checkInDate?: string
  checkOutDate?: string
  offer: App.HotelOffer
  rooms: Array<OfferPageRoom>
  rateListId?: string
  roomListId?: string
  currentRoom: OfferPageRoom
  onOptionSelect: (packageOption: App.HotelPackage) => void
  flightsEnabled: boolean
  isFlightsSelected: boolean
  variant: 'stacked' | 'carousel'
  availabilities: ReturnType<typeof getRemainingAvailabilityByPackage>
  currentlySelectedOption?: App.OfferPackageOptionView
}

function IndividualRoomSelectRoomOption(props: Props) {
  const {
    packageNumber,
    packageView,
    checkInDate,
    checkOutDate,
    offer,
    rooms,
    rateListId: _rateListId,
    roomListId: _roomListId,
    currentRoom,
    onOptionSelect,
    flightsEnabled,
    isFlightsSelected,
    availabilities,
    currentlySelectedOption,
    variant,
  } = props

  const departureTooEarly = useMemo(() => {
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInDate])

  const { totalFlightPrice } = useOfferPageFlightPrice()
  const roomPax = sumUpOccupancies([currentRoom])
  const totalPax = sumUpOccupancies(rooms)
  const flightPriceProportion = roomPax / totalPax

  const flightPrice = totalFlightPrice ? totalFlightPrice * flightPriceProportion : undefined

  const hasFlights = !!flightPrice

  const showHotelAndFlightsToggle = flightsEnabled
  const _hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected

  const _canShowFlightBundlePrice = flightsEnabled && hasFlights && !departureTooEarly

  const isSelected = packageView.package.id === currentRoom.roomId && packageView.package.roomRate.id === currentRoom.rateId

  const _isDesktopLayout = useScreenSizeUp('tablet')

  const hasAvailability = (() => {
    if (packageView.package.uniqueKey === currentlySelectedOption?.package.uniqueKey) {
      return true
    } else {
      return availabilities.pkgOptionAvailableRooms[packageView.package.uniqueKey] > 0 && availabilities.roomTypeAvailableRooms[packageView.package.roomType.id] > 0
    }
  })()

  const onSelect = useCallback(() => {
    onOptionSelect(packageView.package)
  }, [onOptionSelect, packageView.package])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView, flightPrice: totalFlightPrice, skip: !isFlightsSelected }),
  ], [offer, packageView, isFlightsSelected, currentRegionCode, totalFlightPrice])

  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = packageView.package?.ignoreDynamicCancellationPolicy ?? false

  return <AccommodationIndividualSelectRoomOptionTile isSelected={isSelected}>
    <AccommodationIndividualSelectRoomOptionTileDetails
      variant={variant}
      roomPackage={packageView.package}
      packageNumber={packageNumber}
      cancellationPolicy={<>
        {offer.type === OFFER_TYPE_ALWAYS_ON && <TAOCancellationPolicies
          offerId={offer.id}
          useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
          timezoneOffset={offer.property?.timezoneOffset}
          uniqueKey={packageView.package.uniqueKey}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          occupants={[currentRoom]}
          cancellationPolicy={packageView.package?.roomRate?.cancellationPolicy}
          offerType={offer.type}
        />}
        {offer.type === OFFER_TYPE_HOTEL && <FlashCancellationPolicies
          checkInDate={checkInDate}
          propertyTimezoneOffset={offer.property?.timezoneOffset}
          cancellationPolicy={packageView.package.roomRate?.cancellationPolicy}
        />}
      </>}
    >
      <MultipleRoomsInclusions offer={offer} packageOption={packageView} />
      <LoyaltyPointsWrapper>
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="exact"
        />
      </LoyaltyPointsWrapper>
    </AccommodationIndividualSelectRoomOptionTileDetails>

    <WalledContent
      enforced={offer.walledGarden}
      wall={<AccommodationIndividualSelectRoomOptionTilePricing>
        {hasAvailability && <AccommodationIndividualSelectRoomOptionTileCTA>
          {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />} nonInteractable>Selected</TextButton>}
          {!isSelected && <TextButton kind="secondary" onClick={onSelect}>Select</TextButton>}
        </AccommodationIndividualSelectRoomOptionTileCTA>}

        {!hasAvailability && <AccommodationIndividualSelectRoomOptionTileMessage>
          <MessageBanner kind="warning" density="compact">
            No {getPlural(offer.saleUnit)} left
          </MessageBanner>
        </AccommodationIndividualSelectRoomOptionTileMessage>}
      </AccommodationIndividualSelectRoomOptionTilePricing>}
    >
      <AccommodationIndividualSelectRoomOptionTilePricing>
        <MultipleRoomsBookingModalPriceDetails
          room={currentRoom}
          packageView={packageView}
          isFlightsSelected={isFlightsSelected}
          isFlightsEnabled={flightsEnabled}
          flightPrice={totalFlightPrice}
        />
      </AccommodationIndividualSelectRoomOptionTilePricing>

      {hasAvailability && <AccommodationIndividualSelectRoomOptionTileCTA>
        {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />} nonInteractable>Selected</TextButton>}
        {!isSelected && <TextButton kind="secondary" onClick={onSelect}>Select</TextButton>}
      </AccommodationIndividualSelectRoomOptionTileCTA>}

      {!hasAvailability && <AccommodationIndividualSelectRoomOptionTileMessage>
        <MessageBanner kind="warning" density="compact">
          No {getPlural(offer.saleUnit)} left
        </MessageBanner>
      </AccommodationIndividualSelectRoomOptionTileMessage>}
    </WalledContent>
  </AccommodationIndividualSelectRoomOptionTile>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'offer'
  | 'rooms'
  | 'flightsEnabled'
  | 'isFlightsSelected'
  | 'availabilities'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
  availabilities: getRemainingAvailabilityByPackage(state, appState),
}), IndividualRoomSelectRoomOption)
